import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Download, Calendar, Users, TrendingUp } from 'lucide-react';

const Reports = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearlyTotals, setYearlyTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchYearlyTotals();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchYearlyTotals();
    }
  }, [selectedYear]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => u.role === 'employee' || u.role === 'admin'));
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const fetchYearlyTotals = async () => {
    try {
      const response = await axios.get(`${API}/reports/yearly-totals`, {
        params: { year: selectedYear }
      });
      setYearlyTotals(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Jahrestotale');
    }
  };

  const handleExportCSV = async (type) => {
    setLoading(true);
    try {
      const params = {};
      
      if (type === 'monthly') {
        if (!selectedUser) {
          toast.error('Bitte wählen Sie einen Mitarbeiter aus');
          return;
        }
        params.user_id = selectedUser;
        params.month = selectedMonth;
        params.year = selectedYear;
      } else if (type === 'yearly') {
        if (!selectedUser) {
          toast.error('Bitte wählen Sie einen Mitarbeiter aus');
          return;
        }
        params.user_id = selectedUser;
        params.year = selectedYear;
      } else if (type === 'all') {
        params.year = selectedYear;
      }

      const response = await axios.get(`${API}/reports/export/csv`, {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zeiterfassung_${type}_${selectedYear}_${selectedMonth || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export erfolgreich!');
    } catch (error) {
      toast.error('Fehler beim Export');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'Januar' },
    { value: 2, label: 'Februar' },
    { value: 3, label: 'März' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Dezember' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Berichte & Exporte</h1>
          <p className="text-gray-600 mt-2">Exportieren Sie Zeiterfassungsdaten</p>
        </div>

        {/* Yearly Totals Overview */}
        {yearlyTotals && (
          <Card className="card-hover border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Jahrestotale {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Gesamtstunden aller Mitarbeiter</p>
                  <p className="text-5xl font-bold text-blue-600">
                    {yearlyTotals.grand_total_hours?.toFixed(2)}h
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 mb-3">Mitarbeiter-Übersicht</h3>
                {yearlyTotals.users?.map((user) => (
                  <div key={user.user_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`yearly-total-${user.user_id}`}>
                    <div>
                      <p className="font-medium text-gray-900">{user.first_name} {user.last_name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{user.total_hours?.toFixed(2)}h</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Export */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Monatlicher Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Mitarbeiter</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger data-testid="monthly-user-select">
                    <SelectValue placeholder="Mitarbeiter auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monat</Label>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Jahr</Label>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger data-testid="year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={() => handleExportCSV('monthly')}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="export-monthly-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Monatsbericht exportieren (CSV)
              </Button>
            </CardContent>
          </Card>

          {/* Yearly Export */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Jährlicher Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Mitarbeiter</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger data-testid="yearly-user-select">
                    <SelectValue placeholder="Mitarbeiter auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Jahr</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={() => handleExportCSV('yearly')}
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="export-yearly-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Jahresbericht exportieren (CSV)
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* All Users Export */}
        <Card className="card-hover border-l-4 border-l-green-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Alle Mitarbeiter exportieren
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Exportieren Sie die Zeiterfassungsdaten aller Mitarbeiter für das ausgewählte Jahr.
            </p>
            
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label>Jahr</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={() => handleExportCSV('all')}
                className="bg-green-600 hover:bg-green-700"
                disabled={loading}
                data-testid="export-all-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Alle exportieren (CSV)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Export-Informationen</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• CSV-Dateien können in Excel, Google Sheets oder anderen Tabellenkalkulationen geöffnet werden</li>
                  <li>• Exportierte Daten enthalten Datum, Mitarbeiter, Start-/Endzeit, Projekt, Tätigkeit und Stunden</li>
                  <li>• Pausen werden automatisch von der Gesamtarbeitszeit abgezogen</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Reports;
